import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { AlertService } from '../shared/alert/alert.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ParamContext } from '../shared/service/param.context';
import { HelpService } from '../shared/service/help.service';
import { DesignCrossesContext } from './design-crosses.context';
import { HELP_DESIGN_CROSSES } from '../app.constants';
import { NavTab } from '../shared/nav/tab/nav-tab.model';
import { CrossType, PreviewCrossesModel } from '../shared/study/model/preview-crosses.model';
import { DesignCrossesTreeComponent } from '../shared/tree/design-crosses/design-crosses-tree.component';
import { CreatePreviewCrossesRequestModel } from '../shared/study/model/create-preview-crosses-request.model';
import { CrossService } from '../shared/study/service/cross.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { CrossingSettingsModalComponent } from './crossing-settings-modal.component';
import { UrlService } from '../shared/service/url.service';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { Program } from '../shared/program/model/program';
import { StudyEditorService } from '../study-editor/study-editor.service';

@Component({
    selector: 'jhi-design-crosses',
    templateUrl: './design-crosses.component.html'
})
export class DesignCrossesComponent implements OnInit, OnDestroy {

    helpLink: string;
    isLoading = false;
    isGeneratingCrosses = false;
    isStudyListActive = false;
    listId: number;
    activeListName: string;
    activeStudyId: number;
    studyName: string;
    tabs: NavTab[] = [];
    selectedCrossingMethod: string;

    makeReciprocal: boolean;
    excludeSelf = true;

    subscriptionManager = new SubscriptionsManager();

    UNKNOWN_MALE = CrossType.UNKNOWN_MALE;

    crossingMethods = [
        { id: CrossType.MULTIPLY, text: 'design-crosses.crossing.method.multiply' },
        { id: CrossType.TOP_TO_BOTTOM, text: 'design-crosses.crossing.method.top.to.bottom' },
        { id: CrossType.UNKNOWN_MALE, text: 'design-crosses.crossing.method.unknown.male' },
        { id: CrossType.MULTIPLE_MALE, text: 'design-crosses.crossing.method.multiple.male' },
        { id: CrossType.MULTIPLE_PARENT, text: 'design-crosses.crossing.method.multiple.parent' }
    ];



    constructor(private route: ActivatedRoute,
                public context: DesignCrossesContext,
                private eventManager: JhiEventManager,
                private crossService: CrossService,
                private languageService: JhiLanguageService,
                private alertService: AlertService,
                public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private paramContext: ParamContext,
                private helpService: HelpService,
                private router: Router,
                private urlService: UrlService,
                private studyEditorService: StudyEditorService) {
        this.paramContext.readParams();
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit() {
        const params = this.route.snapshot.queryParams;
        this.context.studyId = params['studyId'];
        this.studyName = params['studyName'];
        this.activeStudyId = this.context.studyId;
        this.context.studyName = this.studyName;

        this.context.openedDirectlyFromNewStudyEditor = params['openedDirectlyFromNewStudyEditor'] || false;

        this.setStudyListActive();

        // Get helplink url
        if (!this.helpLink || !this.helpLink.length) {
            this.helpService.getHelpLink(HELP_DESIGN_CROSSES).toPromise().then((response) => {
                this.helpLink = response.body;
            }).catch((error) => {});
        }
    }

    setActive(listId: number) {
        this.isStudyListActive = false;
        this.activeStudyId = null;

        this.tabs.forEach((tab: NavTab) => {
            tab.active = false;
        });
        const listTab = this.tabs.find((tab: NavTab) => tab.id === listId);
        if (listTab) {
            listTab.active = true;
            this.context.activeListName = listTab.name;
        }
        this.context.activeGermplasmListId = listId;
    }

    setStudyListActive() {
        this.isStudyListActive = true;
        this.activeStudyId = this.context.studyId;
        this.listId = null;
        this.tabs.forEach((tab: NavTab) => tab.active = false);
        this.context.activeGermplasmListId = null;
    }

    closeTab(tab: NavTab) {
        this.tabs.splice(this.tabs.indexOf(tab), 1);
        if (tab.active) {
            this.isStudyListActive = false;
        }
    }

    trackId(index: number, item: NavTab) {
        return item.id;
    }

    closeTabs($event) {
        $event.preventDefault();
        this.tabs = [];
        if (this.isStudyListActive) {
            this.setStudyListActive();
            this.router.navigate(['/design-crosses'], { queryParamsHandling: 'merge' });
        }
    }

    browseList($event) {
        $event.preventDefault();

        this.modalService.open(DesignCrossesTreeComponent as Component, { size: 'xl', backdrop: 'static' })
            .result.then((germplasmLists) => {
                if (germplasmLists && germplasmLists.length > 0) {
                    germplasmLists.forEach((germplasmList) => {
                        const germplasmListId = parseInt(germplasmList.id, 10);
                        if (!this.exists(germplasmListId)) {
                            this.tabs.push(new NavTab(germplasmListId, germplasmList.name, false));
                        }
                    });

                    this.listId = parseInt(germplasmLists[germplasmLists.length - 1].id, 10);
                    this.setActive(this.listId);
                }
                this.activeModal.close();
            }, () => this.activeModal.dismiss());
    }

    back($event) {
        if (this.context.openedDirectlyFromNewStudyEditor) {
            this.studyEditorService.navigateToTab(this.context.studyId, 'study-settings');
        }
    }

    continue($event) {
        this.modalService.open(CrossingSettingsModalComponent as Component, { size: 'xl', backdrop: 'static' })
            .result.then(() => {
                this.activeModal.close();
            }, () => this.activeModal.dismiss());
    }

    generateCrosses($event) {
        this.isGeneratingCrosses = true;
        this.context.crossType = this.selectedCrossingMethod;
        if (this.selectedCrossingMethod === CrossType.UNKNOWN_MALE) {
            this.eventManager.broadcast({ name: 'clearMaleParents' });
        }
        const request = new CreatePreviewCrossesRequestModel(this.context.studyId, this.context.selectedFemaleParents, this.context.selectedMaleParents,
            this.context.crosses, this.selectedCrossingMethod, this.makeReciprocal, this.excludeSelf);
        this.subscriptionManager.add = this.crossService.createPreviewCrosses(this.context.studyId, request)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe(
                (res: PreviewCrossesModel[]) => {
                    this.isGeneratingCrosses = false;
                    res.sort((a, b) => a.entryNumber > b.entryNumber ? 1 : -1);
                    this.context.crosses =  res;
                    this.eventManager.broadcast({ name: 'addToCrosses' });
                }, () => this.isGeneratingCrosses = false);
    }

    disableContinueButton() {
        return this.isLoading || this.context.crosses.length === 0;
    }

    private exists(listId: number) {
        return this.tabs.some((tab: NavTab) => tab.id === listId);
    }

    disableGenerateCrossesButton(): boolean {
        return this.isGeneratingCrosses ||
            !this.selectedCrossingMethod ||
            !this.selectedCrossingMethod.trim() ||
            (!this.context.selectedFemaleParents.length && !this.context.selectedMaleParents.length);
    }

    disableExcludeSelfCheckBox(): boolean {
        return this.selectedCrossingMethod === CrossType.UNKNOWN_MALE || this.selectedCrossingMethod === CrossType.MULTIPLE_PARENT;
    }
}
